import React, { useRef, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import * as VideoActions from '../../stores/actions/video';
import SelectSmall from '../general-components/select-small';
import AudioMeter from '../general-components/audio-meter';
import Colors from '../../utils/colors';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import styled from 'styled-components';
import { AVContext } from '../../contexts/audio-video';
import useUserActions from '../../hooks/use-user-actions';
import EffectsTab from '../right-panel/user-menus/effects/effects';
import CameraOff from '../right-panel/user-menus/camera-off';
import useUpdateDevices from '../../hooks/devices/use-update-devices';
import { selectVBG } from '../../stores/actions/content';
import LoadingSpinner, { LoadingSpinnerType } from '../general-components/loading-spinner';
import useDeviceList from '../../hooks/devices/use-device-list';
import SoundTestWidget from '../general-components/devices/sound-test-widget';

const audioTest = require('../../audio/brandlive.mp3');

const Container = styled.div`
	width: 100%;
	display: grid;
	grid-template-rows: 316px 52px;
	grid-template-columns: 1fr 112px 1fr 388px;
	column-gap: 72px;
	row-gap: 24px;
	margin-bottom: 24px;
`;

const VideoElContainer = styled.div`
	width: 560px;
	max-width: 560px;
	height: 100%;
	grid-column: 1 / 4;
	clip-path: inset(0 0 0 0 round 12px);
	overflow: hidden;
	aspect-ratio: 16 / 9;
	background: #26242B;
	z-index: 1;
	position: relative;
`;

const ScrollContainer = styled.div`
	overflow-y: scroll;
	position: relative;
	height: 96%;
`;

const GradientH3 = styled.h3`
	text-transform: none !important;
	background: linear-gradient(109deg, ${Colors.pinkLemonadePink} -4.67%, ${Colors.lemon} 69.07%, ${Colors.paleGreen} 120.95%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	margin: 0 0 24px 0;
	
	display: flex;
	align-items: center;
	
	.icon {
		margin-right: 8px;
	}
		
	&.small-gradient {
		background: linear-gradient(150deg, ${Colors.pinkLemonadePink} -4.67%, ${Colors.lemon} 140%, ${Colors.paleGreen} 120.95%);
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		margin: 0;
	}
	
`;

const DeviceSettings = styled.div`
	grid-column: 4;
	grid-row: 1 / 3;
	font-family: Poppins;	
	position: relative;
	padding-bottom: 24px;
	
	h6 {
		display: flex;
		text-align: left;

		span {
			margin-left: 8px;
			line-height: 18px;
		}
	}

	transition: all 250ms ease-in;

	&.not-active {
		left: 100%;
		opacity: 0;
		pointer-events: none;
	}

	&.active {
		left: 0;
		opacity: 1;
		pointer-events: auto;
	}
`;

const MuteControls = styled.div`
	grid-column: 2;
	grid-row: 2;
`;

const EffectsButton = styled.button`
	justify-self: end;
	grid-column: 3;
	grid-row: 2;
	background: none;
	color: #908CA0;
	background-color: #26242B;
	transition: all 150ms ease-in;
	border-radius: 30px;
	width: 100%;
	max-width: fit-content;
	display: flex;
	align-items: center;
	border: double 2px transparent;

	.icon {
		margin-bottom: 1px;
		margin-right: 8px;
	}

	&:hover, &:active {
		color: #E5E4EC;
		path {
			fill: #E5E4EC !important;
		}
	}

	&.highlight-rainbow {
		border: double 2px transparent;
		background-image: linear-gradient(#24231B, #24231B), 
						linear-gradient(90deg, #FFFD62 0%, ${Colors.pinkLemonadePink} 100%);
		background-origin: border-box;
		background-clip: padding-box, border-box;

		span {
			background: linear-gradient(90deg, #FFFD62 0%, ${Colors.pinkLemonadePink} 100%);
			background-clip: text;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
		}

		path {
			fill: #FFF97D !important;
		}
	}
`;

const RemoveEffectsButton = styled.button`
	border-radius: 100px;
	background: rgba(255, 255, 255, 0.05);
	color: #908CA0;
	border: none;
	padding: 8px 16px;
	display: flex;
	align-items: center;
	margin-right: 16px;

	.icon {
		margin-right: 8px;
	}
	&:hover {
		background: rgba(255, 255, 255, 0.1);
	}
	&:active {
		background: rgba(255, 255, 255, 0.05);
	}
`;

const maxes: React.CSSProperties = {maxHeight: '100%', maxWidth: '100%'}

interface OwnProps {
	publisher?: OT.Publisher,
	videoEl?: HTMLVideoElement
	enterConferenceRoomFlow: () => void;
	next: () => void;
	nextButtonDisabled: boolean;
}

export default function DeviceSelection(props: OwnProps) {
	const videoDevices = useSelector((s: GS) => s.VideoReducer.videoDevices);
	const audioDevices = useSelector((s: GS) => s.VideoReducer.audioDevices);
	const audioOutputs = useSelector((s: GS) => s.VideoReducer.audioOutputs);
	const selectedVBG = useSelector((s: GS) => s.ContentReducer.selectedVBG);
	const dispatch = useDispatch();

	const {
		publisher,
		videoEl,
		enterConferenceRoomFlow,
		next,
		nextButtonDisabled
	} = props;
	const {updateMediaDevices} = useUpdateDevices();

	const {
		micOpts,
		outputOpts,
		videoOpts,
		selectedMicOption,
		selectedOutputOption,
		selectedVideoOption,
	} = useDeviceList();

	const { getAV } = useContext(AVContext);
	const { audio, video } = getAV();

	const [effectsOpen, setEffectsOpen] = useState(false);

	const [hasCam, hasMic] = useMemo(() => {
		const stream = videoEl?.srcObject as MediaStream;
		return [
			!!stream?.getVideoTracks().length,
			!!stream?.getAudioTracks().length,
		]
	}, [videoEl]);

	const {
		toggleAudio,
		toggleVideo,
	} = useUserActions();

	async function selectMic(val: string) {
		const device = audioDevices.find((d: MediaDeviceInfo) => d.deviceId === val);
		if (publisher && device) {
			publisher.setAudioSource(val);
			dispatch(VideoActions.setSelectedAudio(device));
		}
	}

	async function selectSpeaker(val: string) {
		const device = audioOutputs.find((device: MediaDeviceInfo) => device.deviceId === val);
		if (device && publisher) {
			publisher.setVideoSource(val);
			dispatch(VideoActions.setSelectedAudioOutput(device));
		} 
	}

	async function selectCamera(val: string) {
		const device = videoDevices.find((d: MediaDeviceInfo) => d.deviceId === val);
		if (device && publisher) {
			publisher.setVideoSource(val);
			dispatch(VideoActions.setSelectedVideo(device));
		} 
	}

	function attachSRCObject(ref: HTMLVideoElement | null) {
		if (ref && videoEl && ref.srcObject !== videoEl.srcObject) {
			ref.srcObject = videoEl.srcObject;
		}
	}

	useEffect(() => {
		const doUpdate = () => updateMediaDevices();
		navigator.mediaDevices.addEventListener('devicechange', doUpdate);
		return () => {
			navigator.mediaDevices.removeEventListener('devicechange', doUpdate);
		}
	}, []);

	return (
		<div className="panel teal-green custom-scrollbar">
			<Container>
				<VideoElContainer>
					{!videoEl ? <LoadingSpinner 
						height={36}
						width={36} 
						type={LoadingSpinnerType.Gradient}
						style={{background: '#26242B'}}
					/> : null}
					<video 
						muted
						autoPlay
						playsInline
						ref={attachSRCObject}
						hidden={!video}
						style={{ transform: 'scaleX(-1)', height: '100%', ...maxes }}
					/>
					{(!video && videoEl) ? <CameraOff style={{...maxes}}/> : null}
				</VideoElContainer>
				<DeviceSettings className={effectsOpen ? 'not-active' : 'active'}>
					<GradientH3 className="bl-font-header">Choose your devices</GradientH3>
					<div>
						<h6 className="bl-font-header margin-bottom-12">
							<Icon name={ICONS.SOUND} color={COLORS.WHITE} size={16}></Icon> <span className="header-text">Sound</span>
						</h6>
						<div className="option-row">
							<SelectSmall
								onChange={selectMic}
								label={'Microphone'}
								dropdown={true}
								showLabel={true}
								invalid={!(audioDevices.length > 0)}
								disabled={!(audioDevices.length > 0)}
								optionHeight={46}
								options={micOpts}
								selected={selectedMicOption}
							/>
							<div className={classNames('option-row-buttons', audioDevices.length === 0 && 'disabled')}>
								<Icon name={ICONS.MICROPHONE_ON} size={21} color={COLORS.WHITE}></Icon>
								<AudioMeter
									inputStream={videoEl?.srcObject as MediaStream}
									color={Colors.paleGreen}
								/>
							</div>
						</div>
						<div className="option-row">
							<SelectSmall
								onChange={selectSpeaker}
								label={'Speakers'}
								dropdown={true}
								showLabel={true}
								invalid={!(audioOutputs.length > 0)}
								disabled={!(audioOutputs.length > 0)}
								optionHeight={46}
								options={outputOpts}
								selected={selectedOutputOption}
							/>
							<SoundTestWidget />
						</div>
					</div>
					<div className="video-section">
						<h6 className="bl-font-header margin-bottom-12">
							<Icon name={ICONS.CAMERA_ON} size={16} color={COLORS.WHITE}></Icon> <span className="header-text">Video</span>
						</h6>
						<div className="option-row">
							<SelectSmall
								onChange={selectCamera}
								label={'Camera'}
								dropdown={true}
								showLabel={true}
								invalid={!(videoDevices.length > 0)}
								disabled={!(videoDevices.length > 0)}
								optionHeight={46}
								options={videoOpts}
								selected={selectedVideoOption}
							/>
						</div>
					</div>
				</DeviceSettings>
				<DeviceSettings className={effectsOpen ? 'active' : 'not-active'}>
					<div className='justify-content-space-between margin-bottom-small'>
						<GradientH3 className="bl-font-header small-gradient">
							<div onClick={() => setEffectsOpen(false)} className='align-items-center' style={{cursor: 'pointer'}}>
								<Icon name={ICONS.ARROW_LEFT} color={COLORS.HOT_PINK} size={24} />Effects
							</div>
						</GradientH3>
						<RemoveEffectsButton disabled={!selectedVBG} onClick={() => {
							publisher?.clearVideoFilter();
							dispatch(selectVBG(null));
						}}>
							<Icon name={ICONS.CLOSE} color={COLORS.TEXT_GRAY_400} size={12}/>
							<span className='bl-font-l2'>Remove Effects</span>
						</RemoveEffectsButton>
					</div>
					<ScrollContainer className='feathered-scroll-container'>
						<EffectsTab bespokePublisher={publisher}/>
						</ScrollContainer>
				</DeviceSettings>
				<MuteControls>
					<div className='align-items-center tab-row justify-content-space-between gap-small'>
						<button
							className={classNames(
								'padding-medium round text-white',
								video ? 'background-lighter' : 'background-red',
							)}
							hidden={!hasCam}
							onClick={(e) => {
								toggleVideo();
								e.currentTarget.blur();
							}}>
							<Icon
								name={video ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
								color={COLORS.WHITE}
								size={18}
							/>
						</button>
						<button
							className={classNames(
								'padding-medium round text-white',
								audio ? 'background-lighter' : 'background-red',
							)}
							hidden={!hasMic}
							onClick={(e) => {
								toggleAudio();
								e.currentTarget.blur();
							}}>
							<Icon
								name={audio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
								color={COLORS.WHITE}
								size={18}
							/>
						</button>
					</div>
				</MuteControls>
				<EffectsButton 
					hidden={!hasCam}
					onClick={() => setEffectsOpen(prev => !prev)}
					className={classNames(
						'button-greenroom-basic padding-button-medium-wide',
						effectsOpen ? 'highlight-rainbow' : ''
					)}
				>
					<Icon name={ICONS.SPARKLE} size={16} color={
						COLORS.TEXT_GRAY_400
					}/><span>Effects</span>
				</EffectsButton>
			</Container>
			<div className='button-container'>
				<button onClick={() => enterConferenceRoomFlow()}>Use conference room</button>
				<button
					disabled={nextButtonDisabled}
					className={'next'}
					onClick={next}>
						Continue
				</button>
			</div>
		</div>
	);
}
